import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const faqs = [
  {
    question: 'Is the Campus2Container program really free?',
    answer: 'Yes, absolutely! The entire 6-week training program is completely free for eligible students and NYSC members. This includes live classes, recorded sessions, materials, and certificate of participation.'
  },
  {
    question: 'What are the eligibility requirements?',
    answer: 'You must be either a final-year student at any Nigerian tertiary institution or a current/recent NYSC member. You\'ll need to provide valid Student ID or NYSC ID during registration.'
  },
  {
    question: 'How long is the training program?',
    answer: 'The program runs for 6 weeks with structured modules covering different aspects of export business. Each week focuses on specific topics with both live and recorded sessions.'
  },
  {
    question: 'Will I get a certificate?',
    answer: 'Yes, you\'ll receive a free Certificate of Participation upon completing the program. Premium certificates with enhanced credibility are available for ₦3,000.'
  },
  {
    question: 'Do I need any prior business experience?',
    answer: 'No prior experience is required! The program is designed for beginners and will take you from basic concepts to practical export knowledge.'
  },
  {
    question: 'How are the classes conducted?',
    answer: 'Classes are conducted online through live sessions and you\'ll also have access to recorded sessions. All communication and updates happen through our dedicated Telegram group.'
  },
  {
    question: 'What happens after completing the program?',
    answer: 'After completion, you can access mentorship programs, export setup support, and other premium services to help you launch your actual export business.'
  },
  {
    question: 'How many students are accepted per cohort?',
    answer: 'We maintain small cohort sizes to ensure quality interaction and personalized attention. Spaces are limited and filled on a first-come, first-served basis.'
  }
];

const C2CFAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-gray-900 sm:text-4xl"
          >
            Frequently Asked Questions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-xl text-gray-600"
          >
            Everything you need to know about Campus2Container
          </motion.p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="text-lg font-medium text-gray-900 pr-4">
                  {faq.question}
                </span>
                {openIndex === index ? (
                  <Minus className="h-5 w-5 text-blue-600 flex-shrink-0" />
                ) : (
                  <Plus className="h-5 w-5 text-blue-600 flex-shrink-0" />
                )}
              </button>
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: openIndex === index ? 'auto' : 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-gray-600 mb-4">
            Still have questions? We're here to help!
          </p>
          <a
            href="mailto:info@lsem.org.ng"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Contact our support team
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default C2CFAQ;